import { useEffect, useMemo, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import {
  computePlates,
  DEFAULT_BARBELL_KG,
  DEFAULT_PLATE_INVENTORY,
  type Exercise,
  type PlateCalculation,
  type PlateInventoryEntry,
} from '@jackedlogs/shared';

import {
  BAR_OPTIONS_KG,
  loadPlateBarKg,
  loadPlateInventory,
  persistPlateBarKg,
  persistPlateInventory,
} from './state/plateConfig';
import { useTheme } from './theme';
import { parseLocaleNumber } from './parseLocaleNumber';
import { makeStyles } from './plateCalcModalStyles';

// Plate-loading helper. Mounted from the per-entry "Plates" button.
// Pure presentation around `computePlates` from packages/shared — the
// user types a target kg (pre-seeded with the most recent set's weight
// from the row that opened the modal), picks the bar from a fixed
// dropdown (20/15/10/7.5/7/5/0), and edits the per-kg pair count of
// the home plate set. Both prefs persist to AsyncStorage so the modal
// remembers them across sessions and across workouts.

type Props = {
  visible: boolean;
  initialKg: number;
  /** Exercise the modal was opened from — feeds the bar default
   *  (`Exercise.defaultBarKg`) and the loading mode
   *  (`Exercise.barLoading`, 'standard' / 'landmine'). Optional so the
   *  modal still works in flows that don't have a catalog row to hand. */
  exercise?: Exercise | null;
  /** Bodyweight mode (2026-05-23 P.M.). Shows a sign toggle (Add / Assist)
   *  so the row's EXTRA value can be loaded as positive (weighted pull-up
   *  belt) or negative (band-assisted). When false, the modal works as
   *  before — pure plate calculator for barbell loading. */
  isBodyweight?: boolean;
  /** Pre-seeded sign for bodyweight mode — mirrors set.extraNegative so
   *  reopening the modal on an already-loaded set restores the previous
   *  choice. Ignored when isBodyweight=false. */
  initialNegative?: boolean;
  onClose: () => void;
  /** When provided, the modal renders an Apply CTA that writes the
   *  current target back to the caller. Signed: positive for add /
   *  weighted, negative for assist (only relevant in bodyweight mode).
   *  In weighted mode the value is always positive. */
  onApply?: (signedKg: number) => void;
};

const formatKg = (n: number): string => {
  if (!Number.isFinite(n)) return '0';
  const s = n.toFixed(2);
  return s.replace(/\.?0+$/, '');
};

const inventoryKgList = DEFAULT_PLATE_INVENTORY.map((e) => e.kg);

export function PlateCalcModal({
  visible,
  initialKg,
  exercise = null,
  isBodyweight = false,
  initialNegative = false,
  onClose,
  onApply,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [target, setTarget] = useState('');
  const [barKg, setBarKg] = useState<number>(DEFAULT_BARBELL_KG);
  const [inventory, setInventory] = useState<PlateInventoryEntry[]>(() =>
    DEFAULT_PLATE_INVENTORY.map((e) => ({ ...e })),
  );
  const [editingInventory, setEditingInventory] = useState(false);
  // Bodyweight sign: positive = weighted (extra plate on belt), negative
  // = assist (band / machine). Synced from set.extraNegative on open via
  // the visible-prop effect below.
  const [negative, setNegative] = useState(false);
  const isLandmine = exercise?.barLoading === 'landmine';

  // Reseed the input on every open so consecutive opens on different
  // rows pick up the latest weight value. Bar gets re-seeded too: the
  // exercise's defaultBarKg wins (curl bar 7, landmine ~10, etc.), and
  // we still let the user override per-open via the chip row. Sign
  // tracks the row's existing extraNegative on bodyweight rows.
  useEffect(() => {
    if (!visible) return;
    setTarget(initialKg > 0 ? formatKg(initialKg) : '');
    if (exercise?.defaultBarKg !== undefined) {
      setBarKg(exercise.defaultBarKg);
    }
    setNegative(isBodyweight && initialNegative);
  }, [visible, initialKg, exercise?.defaultBarKg, isBodyweight, initialNegative]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [b, inv] = await Promise.all([
        loadPlateBarKg(),
        loadPlateInventory(),
      ]);
      if (cancelled) return;
      setBarKg(b);
      setInventory(inv);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelectBar = (kg: number) => {
    setBarKg(kg);
    void persistPlateBarKg(kg);
  };

  const setPairs = (kg: number, pairs: number) => {
    const clamped = Math.max(0, Math.min(99, Math.floor(pairs)));
    setInventory((prev) => {
      const existing = prev.find((p) => p.kg === kg);
      const next = existing
        ? prev.map((p) => (p.kg === kg ? { ...p, pairs: clamped } : p))
        : [...prev, { kg, pairs: clamped }].sort((a, b) => b.kg - a.kg);
      void persistPlateInventory(next);
      return next;
    });
  };

  const inventoryByKg = useMemo(() => {
    const map = new Map<number, number>();
    for (const e of inventory) map.set(e.kg, e.pairs);
    return map;
  }, [inventory]);

  const calc: PlateCalculation = useMemo(() => {
    // parseLocaleNumber so "20,5" on de-DE / fr-FR / it-IT decimal-pad
    // keyboards parses as 20.5 instead of truncating to 20.
    const targetKg = parseLocaleNumber(target);
    if (!Number.isFinite(targetKg)) {
      return { ok: false, reason: 'invalid_input' };
    }
    return computePlates({
      targetKg,
      barKg,
      inventory,
      ...(isLandmine ? { barLoading: 'landmine' as const } : {}),
    });
  }, [target, barKg, inventory, isLandmine]);

  const styles = makeStyles(theme);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <View style={styles.header}>
          <Pressable accessibilityRole="button" onPress={onClose}>
            <Text style={styles.cancel}>{t('mobile.plates.close')}</Text>
          </Pressable>
          <Text style={styles.title}>{t('mobile.plates.title')}</Text>
          {onApply ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('mobile.plates.apply', {
                defaultValue: 'Apply',
              })}
              onPress={() => {
                const num = parseLocaleNumber(target);
                if (!Number.isFinite(num) || num <= 0) {
                  onClose();
                  return;
                }
                const signed = isBodyweight && negative ? -num : num;
                onApply(signed);
              }}
            >
              <Text style={styles.apply}>
                {t('mobile.plates.apply', { defaultValue: 'Apply' })}
              </Text>
            </Pressable>
          ) : (
            <View style={styles.headerSpacer} />
          )}
        </View>
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.inputRow}>
              <Text style={styles.inputLabel}>
                {t('mobile.plates.targetLabel')}
              </Text>
              <TextInput
                style={styles.input}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder="—"
                placeholderTextColor={theme.palette.ink3}
                value={target}
                onChangeText={setTarget}
                autoFocus
              />
              <Text style={styles.unit}>kg</Text>
            </View>
            {isBodyweight ? (
              <View style={styles.signRow}>
                <Text style={styles.sectionLabel}>
                  {t('mobile.plates.signLabel', { defaultValue: 'EXTRA SIGN' })}
                </Text>
                <View style={styles.signChipRow}>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => setNegative(false)}
                    style={({ pressed }) => [
                      styles.signChip,
                      !negative && styles.signChipSelected,
                      pressed && styles.signChipPressed,
                    ]}
                  >
                    <Text
                      style={[
                        styles.signChipText,
                        !negative && styles.signChipTextSelected,
                      ]}
                    >
                      {t('mobile.plates.signAdd', { defaultValue: '+ Weighted' })}
                    </Text>
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => setNegative(true)}
                    style={({ pressed }) => [
                      styles.signChip,
                      negative && styles.signChipSelected,
                      pressed && styles.signChipPressed,
                    ]}
                  >
                    <Text
                      style={[
                        styles.signChipText,
                        negative && styles.signChipTextSelected,
                      ]}
                    >
                      {t('mobile.plates.signAssist', {
                        defaultValue: '− Assist',
                      })}
                    </Text>
                  </Pressable>
                </View>
              </View>
            ) : null}

            <Text style={styles.sectionLabel}>
              {t('mobile.plates.barLabel')}
            </Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.barRow}
              keyboardShouldPersistTaps="handled"
            >
              {BAR_OPTIONS_KG.map((kg) => {
                const selected = barKg === kg;
                return (
                  <Pressable
                    key={kg}
                    accessibilityRole="button"
                    onPress={() => onSelectBar(kg)}
                    style={({ pressed }) => [
                      styles.barChip,
                      selected && styles.barChipSelected,
                      pressed && styles.barChipPressed,
                    ]}
                  >
                    <Text
                      style={[
                        styles.barChipText,
                        selected && styles.barChipTextSelected,
                      ]}
                    >
                      {formatKg(kg)} kg
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            <View style={styles.outputCard}>
              {calc.ok ? (
                <>
                  <Text style={styles.outputHeader}>
                    {calc.mode === 'single'
                      ? t('mobile.plates.loadedEnd', {
                          kg: formatKg(calc.perSideKg),
                        })
                      : t('mobile.plates.perSide', {
                          kg: formatKg(calc.perSideKg),
                        })}
                  </Text>
                  {calc.pairsLoaded.length === 0 ? (
                    <Text style={styles.outputBody}>
                      {t('mobile.plates.barOnly')}
                    </Text>
                  ) : (
                    calc.pairsLoaded.map((p) => (
                      <View key={p.kg} style={styles.plateRow}>
                        <Text style={styles.plateKg}>
                          {formatKg(p.kg)} kg
                        </Text>
                        <Text style={styles.platePairs}>
                          {calc.mode === 'single'
                            ? t('mobile.plates.plates', { count: p.pairs })
                            : t('mobile.plates.pairs', { count: p.pairs })}
                        </Text>
                      </View>
                    ))
                  )}
                  {calc.remainderKg > 0 ? (
                    <Text style={styles.remainder}>
                      {t('mobile.plates.remainder', {
                        kg: formatKg(calc.remainderKg),
                      })}
                    </Text>
                  ) : null}
                </>
              ) : calc.reason === 'below_bar' ? (
                <Text style={styles.outputBody}>
                  {t('mobile.plates.belowBar')}
                </Text>
              ) : (
                <Text style={styles.outputBody}>
                  {t('mobile.plates.invalid')}
                </Text>
              )}
            </View>
            {isLandmine ? (
              <Text style={styles.landmineHint}>
                {t('mobile.plates.landmineHint')}
              </Text>
            ) : null}

            <Pressable
              accessibilityRole="button"
              onPress={() => setEditingInventory((v) => !v)}
              style={styles.toggleInventory}
            >
              <Text style={styles.toggleInventoryText}>
                {editingInventory
                  ? t('mobile.plates.inventoryHide')
                  : t('mobile.plates.inventoryShow')}
              </Text>
            </Pressable>

            {editingInventory ? (
              <View style={styles.inventoryCard}>
                <Text style={styles.inventoryHeader}>
                  {t('mobile.plates.inventoryTitle')}
                </Text>
                {inventoryKgList.map((kg) => {
                  const pairs = inventoryByKg.get(kg) ?? 0;
                  return (
                    <View key={kg} style={styles.inventoryRow}>
                      <Text style={styles.inventoryKg}>
                        {formatKg(kg)} kg
                      </Text>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t('mobile.plates.pairsDecrement')}
                        onPress={() => setPairs(kg, pairs - 1)}
                        style={({ pressed }) => [
                          styles.stepButton,
                          pressed && styles.stepButtonPressed,
                        ]}
                        disabled={pairs <= 0}
                      >
                        <Text style={styles.stepButtonText}>−</Text>
                      </Pressable>
                      <Text style={styles.inventoryPairs}>
                        {t('mobile.plates.pairs', { count: pairs })}
                      </Text>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t('mobile.plates.pairsIncrement')}
                        onPress={() => setPairs(kg, pairs + 1)}
                        style={({ pressed }) => [
                          styles.stepButton,
                          pressed && styles.stepButtonPressed,
                        ]}
                      >
                        <Text style={styles.stepButtonText}>+</Text>
                      </Pressable>
                    </View>
                  );
                })}
              </View>
            ) : null}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}
